import {CommandBar, ICommandBarItemProps, Stack} from "@fluentui/react";
import {FC, useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import "../helpers/AttachMonacoLibs";
import * as monaco from "monaco-editor";
import "../styles/CMElement.css";
import {theme} from "..";

export type SupportCodeLanguage =
    | "pascal"
    | "vb"
    | "javascript"
    | "typescript"
    | "sql"
    | "xml"
    | "ini"
    | "json";

export const codeLanguages: SupportCodeLanguage[] = [
    "pascal",
    "vb",
    "javascript",
    "typescript",
    "sql",
    "xml",
    "ini",
    "json",
];

codeLanguages.forEach((lang) => {
    monaco.languages.register({id: lang});
});

monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
    allowNonTsExtensions: true,
});

const sqlKeywords: string[] = [
    "SELECT",
    "FROM",
    "WHERE",
    "GROUP BY",
    "ORDER BY",
    "HAVING",
    "INSERT INTO",
    "UPDATE",
    "DELETE FROM",
    "CREATE TABLE",
    "ALTER TABLE",
    "DROP TABLE",
    "CREATE DATABASE",
    "ALTER DATABASE",
    "DROP DATABASE",
    "CREATE INDEX",
    "DROP INDEX",
    "AND",
    "OR",
    "NOT",
    "IN",
    "BETWEEN",
    "LIKE",
    "IS NULL",
    "IS NOT NULL",
    "INNER JOIN",
    "LEFT JOIN",
    "RIGHT JOIN",
    "FULL JOIN",
    "CROSS JOIN",
    "UNION",
    "UNION ALL",
    "EXISTS",
    "CASE",
    "WHEN",
    "THEN",
    "ELSE",
    "END",
    "CREATE PROCEDURE",
    "ALTER PROCEDURE",
    "DROP PROCEDURE",
    "EXEC",
    "EXECUTE",
    "CREATE FUNCTION",
    "ALTER FUNCTION",
    "DROP FUNCTION",
    "CREATE VIEW",
    "ALTER VIEW",
    "DROP VIEW",
    "CREATE TRIGGER",
    "ALTER TRIGGER",
    "DROP TRIGGER",
    "CREATE SCHEMA",
    "ALTER SCHEMA",
    "DROP SCHEMA",
    "GRANT",
    "REVOKE",
    "COMMIT",
    "ROLLBACK",
    "SAVEPOINT",
    "BEGIN",
    "DECLARE",
    "SET",
    "GO",
    "USE",
    "PRINT",
    "WHILE",
    "BREAK",
    "CONTINUE",
    "RETURN",
    "IF",
    "TRY",
    "CATCH",
    "THROW",
    "RAISERROR",
    "AS",
    "ASC",
    "DESC",
    "INTO",
    "VALUES",
    "DEFAULT",
    "PRIMARY KEY",
    "FOREIGN KEY",
    "CONSTRAINT",
    "CHECK",
    "UNIQUE",
    "IDENTITY",
    "NULL",
    "TOP",
    "ALL",
    "ANY",
    "SOME",
];

const sqlFunctions: string[] = [
    "AVG()",
    "COUNT()",
    "FIRST()",
    "LAST()",
    "MAX()",
    "MIN()",
    "SUM()",
    "GROUP_CONCAT()",
    "LCASE()",
    "UCASE()",
    "MID()",
    "LEN()",
    "ROUND()",
    "NOW()",
    "FORMAT()",
    "COALESCE()",
    "IFNULL()",
    "ISNULL()",
    "NVL()",
    "NVL2()",
    "CONCAT()",
    "SUBSTR()",
    "INSTR()",
    "TRIM()",
    "LTRIM()",
    "RTRIM()",
    "REPLACE()",
    "REVERSE()",
    "SPACE()",
    "REPLICATE()",
    "CHARINDEX()",
    "PATINDEX()",
    "LEFT()",
    "RIGHT()",
    "LOWER()",
    "UPPER()",
    "ASCII()",
    "CHAR()",
    "UNICODE()",
    "STR()",
    "QUOTENAME()",
    "DEGREES()",
    "EXP()",
    "FLOOR()",
    "LOG()",
    "LOG10()",
    "PI()",
    "POWER()",
    "RADIANS()",
    "RAND()",
    "SIGN()",
    "SIN()",
    "SQRT()",
    "SQUARE()",
    "TAN()",
    "CURDATE()",
    "CURRENT_DATE()",
    "CURTIME()",
    "CURRENT_TIME()",
    "CURRENT_TIMESTAMP()",
    "DATEADD()",
    "DATEDIFF()",
    "DATEPART()",
    "DAY()",
    "GETDATE()",
    "MONTH()",
    "YEAR()",
    "ABS()",
    "ACOS()",
    "ASIN()",
    "ATAN()",
    "ATN2()",
    "CEILING()",
    "COS()",
    "COT()",
];

let globalDatabaseDefinitions: SQLDefinition[] = [];

monaco.languages.registerInlineCompletionsProvider("sql", {
    provideInlineCompletions: (
        model,
        position,
        context,
        token,
    ): monaco.languages.ProviderResult<
        monaco.languages.InlineCompletions<monaco.languages.InlineCompletion>
    > => {
        // Extract the text that has been typed
        var textUntilPosition = model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
        });

        // Match any word that is being typed
        const match = textUntilPosition.match(/(\w+)$/);
        if (!match) return {items: []};

        // Generate suggestions based on the match
        const word = match[1].toUpperCase();
        const suggestions: monaco.languages.InlineCompletion[] = [];

        // // Suggest SQL keywords
        // for (let i = 0; i < sqlKeywords.length; i++) {
        //   const kw = sqlKeywords[i]?.toUpperCase();
        //   if (kw?.toUpperCase().startsWith(word.toUpperCase())) {
        //     const rest = kw.substring(word.length);
        //     suggestions.push({
        //       insertText: rest,
        //       additionalTextEdits: [
        //         {
        //           range: {
        //             startLineNumber: position.lineNumber,
        //             startColumn: position.column,
        //             endLineNumber: position.lineNumber,
        //             endColumn: position.column,
        //           },
        //           text: " ",
        //         },
        //       ],
        //       command: {
        //         id: "editor.action.triggerSuggest",
        //         title: "Suggest",
        //       },
        //       completeBracketPairs: true,
        //       range: {
        //         startLineNumber: position.lineNumber,
        //         startColumn: position.column,
        //         endLineNumber: position.lineNumber,
        //         endColumn: position.column,
        //       },
        //     });
        //   }
        // }

        // // Suggest SQL functions
        // for (let i = 0; i < sqlFunctions.length; i++) {
        //   const f = sqlFunctions[i]?.toUpperCase();
        //   if (f?.startsWith(word.toUpperCase())) {
        //     const rest = f.substring(word.length);
        //     suggestions.push({
        //       insertText: rest,
        //       additionalTextEdits: [
        //         {
        //           range: {
        //             startLineNumber: position.lineNumber,
        //             startColumn: position.column,
        //             endLineNumber: position.lineNumber,
        //             endColumn: position.column,
        //           },
        //           text: " ",
        //         },
        //       ],
        //       command: {
        //         id: "editor.action.triggerSuggest",
        //         title: "Suggest",
        //       },
        //       completeBracketPairs: true,
        //       range: {
        //         startLineNumber: position.lineNumber,
        //         startColumn: position.column,
        //         endLineNumber: position.lineNumber,
        //         endColumn: position.column,
        //       },
        //     });
        //   }
        // }

        const currentWord = model.getWordUntilPosition(position);
        const prevPosition = {
            lineNumber: position.lineNumber,
            column: currentWord.startColumn - 1,
        };
        const prevWord = model.getWordUntilPosition(prevPosition);

        if (
            prevWord.word.toUpperCase() === "FROM" ||
            prevWord.word.toUpperCase() === "JOIN" ||
            prevWord.word.toUpperCase() === "INTO"
        ) {
            const tablesAndViews = globalDatabaseDefinitions.filter(
                (d) =>
                    d.ObjectType === "TABLE" ||
                    d.ObjectType === "VIEW" ||
                    d.ObjectType === "USER_TABLE" ||
                    d.ObjectType === "SQL_INLINE_TABLE_VALUED_FUNCTION",
            );
            for (let i = 0; i < tablesAndViews.length; i++) {
                const t = tablesAndViews[i]?.ObjectName.toUpperCase();
                if (t?.startsWith(word.toUpperCase())) {
                    const rest = t.substring(word.length);
                    suggestions.push({
                        insertText: rest,
                        additionalTextEdits: [
                            {
                                range: {
                                    startLineNumber: position.lineNumber,
                                    startColumn: position.column,
                                    endLineNumber: position.lineNumber,
                                    endColumn: position.column,
                                },
                                text: " ",
                            },
                        ],
                        command: {
                            id: "editor.action.triggerSuggest",
                            title: "Suggest",
                        },
                        completeBracketPairs: true,
                        range: {
                            startLineNumber: position.lineNumber,
                            startColumn: position.column,
                            endLineNumber: position.lineNumber,
                            endColumn: position.column,
                        },
                    });
                }
            }
        }

        if (prevWord.word.toUpperCase() === "EXEC") {
            const procedures = globalDatabaseDefinitions.filter(
                (d) => d.ObjectType === "SQL_STORED_PROCEDURE",
            );
            for (let i = 0; i < procedures.length; i++) {
                const p = procedures[i]?.ObjectName.toUpperCase();
                if (p?.startsWith(word.toUpperCase())) {
                    const rest = p.substring(word.length);
                    suggestions.push({
                        insertText: rest,
                        additionalTextEdits: [
                            {
                                range: {
                                    startLineNumber: position.lineNumber,
                                    startColumn: position.column,
                                    endLineNumber: position.lineNumber,
                                    endColumn: position.column,
                                },
                                text: " ",
                            },
                        ],
                        command: {
                            id: "editor.action.triggerSuggest",
                            title: "Suggest",
                        },
                        completeBracketPairs: true,
                        range: {
                            startLineNumber: position.lineNumber,
                            startColumn: position.column,
                            endLineNumber: position.lineNumber,
                            endColumn: position.column,
                        },
                    });
                }
            }
        }

        if (prevWord.word.toUpperCase() === "SELECT") {
            const scalarFunctions = globalDatabaseDefinitions.filter(
                (d) => d.ObjectType === "SQL_SCALAR_FUNCTION",
            );

            for (let i = 0; i < scalarFunctions.length; i++) {
                const f = scalarFunctions[i]?.ObjectName.toUpperCase();
                if (f?.startsWith(word.toUpperCase())) {
                    const rest = f.substring(word.length);
                    suggestions.push({
                        insertText: rest,
                        additionalTextEdits: [
                            {
                                range: {
                                    startLineNumber: position.lineNumber,
                                    startColumn: position.column,
                                    endLineNumber: position.lineNumber,
                                    endColumn: position.column,
                                },
                                text: " ",
                            },
                        ],
                        command: {
                            id: "editor.action.triggerSuggest",
                            title: "Suggest",
                        },
                        completeBracketPairs: true,
                        range: {
                            startLineNumber: position.lineNumber,
                            startColumn: position.column,
                            endLineNumber: position.lineNumber,
                            endColumn: position.column,
                        },
                    });
                }
            }
        }

        // return { items: suggestions };

        return new Promise((resolve, reject) => {
            resolve({items: suggestions});
        });
    },
    freeInlineCompletions: (completions: any): void => {
    },
});

export interface SQLDefinition {
    ObjectName: string;
    ObjectType: string;
    Parameters: string;
}

interface CMElementProps {
    endData: string;
    startData?: string;
    language: SupportCodeLanguage;
    runButtonId?: string;
    runClicked?: (value: string, selectedText?: string) => Promise<void>;
    runWithoutEditorValue?: boolean;
    saveFile?: (value: string) => Promise<void>;
    onChanged?: (value: string) => void;
    disabled?: boolean;
    minHeight?: string;
    getSQLDefinitions?: () => Promise<SQLDefinition[]>;
    tsDefinitions?: string;
    title?: string;
    commands?: ICommandBarItemProps[];
}

export const CMEditor: FC<CMElementProps> = ({
                                                 endData,
                                                 language,
                                                 saveFile,
                                                 runClicked,
                                                 runWithoutEditorValue,
                                                 onChanged,
                                                 disabled,
                                                 minHeight,
                                                 getSQLDefinitions,
                                                 tsDefinitions,
                                                 title,
                                                 commands,
                                             startData,
                                             runButtonId,
                                         }) => {
    const safeEndData = endData ?? "";
    const safeStartData = startData ?? "";
    const divEl = useRef<HTMLDivElement>(null);
    const editor = useRef<monaco.editor.IStandaloneCodeEditor>();
    const diffEditor = useRef<monaco.editor.IStandaloneDiffEditor>();
    const endModel = useRef<monaco.editor.ITextModel>();
    const startModel = useRef<monaco.editor.ITextModel>();
    const {t} = useTranslation();
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const shouldUseDiffEditor = Boolean(startData);

    useEffect(() => {
        setIsDirty(false);
    }, [endData]);

    useEffect(() => {
        if (tsDefinitions) {
            console.log("adding ts definitions", tsDefinitions);
            monaco.languages.typescript.javascriptDefaults.addExtraLib(
                tsDefinitions,
                "tsDefinitions.d.ts",
            );
        }
    }, [tsDefinitions]);

    useEffect(() => {
        getSQLDefinitions?.().then((defs) => {
            globalDatabaseDefinitions = defs;
        });
    }, [getSQLDefinitions]);

    useEffect(() => {
        if (divEl.current && !editor.current && !diffEditor.current) {
            endModel.current = monaco.editor.createModel(safeEndData, language);
            endModel.current.onDidChangeContent(() => {
                onChanged?.(endModel.current?.getValue() ?? "");
                setIsDirty(safeEndData !== endModel.current?.getValue());
            });
            if (shouldUseDiffEditor) {
                startModel.current = monaco.editor.createModel(safeStartData, language);

                diffEditor.current = monaco.editor.createDiffEditor(divEl.current, {
                    splitViewDefaultRatio: 0.5,
                    enableSplitViewResizing: true,
                    quickSuggestions: {
                        comments: false,
                        other: true,
                        strings: true,
                    },
                    suggest: {
                        showFiles: false,
                        showMethods: true,
                        showFunctions: true,
                        showClasses: true,
                        showVariables: true,
                        showWords: true,
                        showColors: true,
                        showConstants: true,
                        showConstructors: true,
                        showDeprecated: true,
                        showEnumMembers: true,
                        showEnums: true,
                        showEvents: true,
                        showFields: true,
                        showFolders: true,
                        showIcons: true,
                        showInlineDetails: true,
                        showInterfaces: true,
                        showIssues: true,
                        showKeywords: true,
                        showModules: true,
                        showOperators: true,
                        showProperties: true,
                        showReferences: true,
                        showSnippets: true,
                        showStatusBar: true,
                        showStructs: true,
                        showTypeParameters: true,
                        showUnits: true,
                        showUsers: true,
                        showValues: true,
                        shareSuggestSelections: true,
                    },
                    acceptSuggestionOnEnter: "on",
                    acceptSuggestionOnCommitCharacter: true,
                });

                diffEditor.current.setModel({
                    original: startModel.current,
                    modified: endModel.current,
                });
            } else {
                editor.current = monaco.editor.create(divEl.current, {
                    language: language,
                    model: endModel.current,
                    quickSuggestions: {
                        comments: false,
                        other: true,
                        strings: true,
                    },
                    suggest: {
                        showFiles: false,
                        showMethods: true,
                        showFunctions: true,
                        showClasses: true,
                        showVariables: true,
                        showWords: true,
                        showColors: true,
                        showConstants: true,
                        showConstructors: true,
                        showDeprecated: true,
                        showEnumMembers: true,
                        showEnums: true,
                        showEvents: true,
                        showFields: true,
                        showFolders: true,
                        showIcons: true,
                        showInlineDetails: true,
                        showInterfaces: true,
                        showIssues: true,
                        showKeywords: true,
                        showModules: true,
                        showOperators: true,
                        showProperties: true,
                        showReferences: true,
                        showSnippets: true,
                        showStatusBar: true,
                        showStructs: true,
                        showTypeParameters: true,
                        showUnits: true,
                        showUsers: true,
                        showValues: true,
                        shareSuggestSelections: true,
                    },
                    acceptSuggestionOnEnter: "on",
                    acceptSuggestionOnCommitCharacter: true,
                });

                editor.current.onKeyDown((e) => {
                    if (e.keyCode === 27) {
                        e.stopPropagation();
                    }
                });
            }
        }
        return () => {
            editor.current?.dispose();
            editor.current = undefined;
            diffEditor.current?.dispose();
            diffEditor.current = undefined;
            endModel.current?.dispose();
            endModel.current = undefined;
            startModel.current?.dispose();
            startModel.current = undefined;
        };
    }, [language, shouldUseDiffEditor]);

    useEffect(() => {
        const nextEndData = endData ?? "";
        if (endModel.current && nextEndData !== endModel.current.getValue()) {
            endModel.current.setValue(nextEndData);
            setIsDirty(false);
        }
        if (
            startModel.current &&
            startData !== undefined &&
            startData !== null &&
            safeStartData !== startModel.current.getValue()
        ) {
            startModel.current.setValue(safeStartData);
        }
    }, [endData, startData, safeStartData]);

    const saveClicked = useCallback(async () => {
        if (saveFile) {
            const evValue =
                editor.current?.getValue() ?? endModel.current?.getValue();
            if (evValue) {
                try {
                    await saveFile(evValue);
                    setIsDirty(false);
                } catch (e) {
                    console.log(e);
                }
            } else {
                throw new Error("No value");
            }
        }
    }, [editor, saveFile, endModel]);

    const runClickedLocal = useCallback(async () => {
        if (runClicked) {
            if (runWithoutEditorValue) {
                await runClicked("");
                return;
            }

            const evValue =
                editor.current?.getValue() ?? endModel.current?.getValue();
            const selection = editor.current?.getSelection();
            const selectedText = selection
                ? editor.current?.getModel()?.getValueInRange(selection)
                : undefined;
            if (evValue) {
                await runClicked(evValue, selectedText);
            } else {
                throw new Error("No value");
            }
        }
    }, [runClicked, runWithoutEditorValue, editor, endModel]);

    const onKeyDownCapture = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.keyCode === 27 || e.key === "Escape") {
                e.stopPropagation();
            }
        },
        [],
    );

    return (
        <Stack onKeyDown={onKeyDownCapture} title={title}>
            {runClicked || commands?.length || saveFile ? <CommandBar
                title={title}
                items={[
                    ...(runClicked
                        ? [
                            {
                                id: runButtonId,
                                key: "run",
                                text: t("run"),
                                iconProps: {iconName: "Play"},
                                onClick: () => {
                                    runClickedLocal();
                                },
                                disabled: disabled,
                            },
                        ]
                        : []),
                    ...(saveFile
                        ? [
                            {
                                style: isDirty
                                    ? {
                                        backgroundColor: theme.palette.themePrimary,
                                        color: theme.palette.white,
                                    }
                                    : {},
                                key: "save",
                                text: t("save"),
                                iconProps: {
                                    iconName: "Save",
                                    style: {
                                        color: isDirty ? theme.palette.white : undefined,
                                    },
                                },
                                onClick: () => {
                                    saveClicked();
                                },
                                disabled: (!isDirty || disabled) && !startData && !endData,
                            } as ICommandBarItemProps,
                        ]
                        : []),
                    ...(commands ?? []),
                ]}
            /> : null}
            <div
                title={title}
                aria-disabled={disabled}
                className="Editor"
                style={{
                    minHeight: minHeight ?? "80vh",
                    flexGrow: 1,
                    flex: 1,
                }}
                ref={divEl}
            />
        </Stack>
    );
};
